import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useEffect } from "react"
import { FormProvider, type UseFormProps, useForm } from "react-hook-form"

import { CLASSIFICATION_ACCOUNT_TYPES } from "../../constants"
import type { ChartOfAccountsSearchParams } from "../../types/searchParams"

import { AccountTypeFilter } from "./AccountTypeFilter"
import { ClassificationFilter } from "./ClassificationFilter"
import { ReportingAccountTypeFilter } from "./ReportingAccountTypeFilter"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { GlAccountType } from "@/graphql/codegen/graphql"

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: () => void
}

export interface FormValues {
  classification?: string
  accountTypes?: string[]
  reportingAccountType?: string
}

export const FilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const navigate = useNavigate({ from: "/app/admin/accounting/accounts-chart" })
  const currentSearch: ChartOfAccountsSearchParams = useSearch({
    from: "/app/admin/accounting/_layout/accounts-chart",
    strict: true,
  })

  const { classification, accountTypes, reportingAccountType } = currentSearch

  const methods = useForm<FormValues>({
    values: {
      classification,
      accountTypes,
      reportingAccountType,
    },
    mode: "onChange",
  } as UseFormProps<FormValues>)

  const handleClearFilters = async () => {
    methods.reset()
    await navigate({
      search: (prev) => ({
        ...prev,
        classification: undefined,
        accountTypes: undefined,
        reportingAccountType: undefined,
      }),
      replace: true,
    })
  }

  const handleFilterChange = useCallback(
    async (name: keyof ChartOfAccountsSearchParams, value: unknown) => {
      await navigate({
        search: (prev: ChartOfAccountsSearchParams) => {
          const val = Array.isArray(value) && value.length === 0 ? undefined : value
          return {
            ...prev,
            [name]: val as never,
          }
        },
        replace: true,
      })
    },
    [navigate]
  )

  // Auto-deselect account types when classification changes
  const watchedClassification = methods.watch("classification")
  const watchedAccountTypes = methods.watch("accountTypes")

  useEffect(() => {
    if (!watchedClassification) {
      return
    }

    const types = CLASSIFICATION_ACCOUNT_TYPES[watchedClassification as keyof typeof CLASSIFICATION_ACCOUNT_TYPES]
    const validTypes = new Set<GlAccountType>(types || [])

    const currentAccountTypes = watchedAccountTypes || []
    const invalidTypes = currentAccountTypes.filter((type) => !validTypes.has(type as GlAccountType))

    if (invalidTypes.length > 0) {
      const newAccountTypes = currentAccountTypes.filter((type) => validTypes.has(type as GlAccountType))
      handleFilterChange("accountTypes", newAccountTypes.length > 0 ? newAccountTypes : undefined)
    }
  }, [watchedClassification, watchedAccountTypes, handleFilterChange])

  return (
    <SearchFilterDrawer toggleDrawer={toggleDrawer} open={showFilterDrawer} clear={handleClearFilters}>
      <FormProvider {...methods}>
        <Box className='h-full min-w-80'>
          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Classification
            </Typography>
            <Divider />
            <ClassificationFilter onFilterChange={handleFilterChange} />
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Account Type
            </Typography>
            <Divider />
            <AccountTypeFilter onFilterChange={handleFilterChange} />
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Reporting Account Type
            </Typography>
            <Divider />
            <ReportingAccountTypeFilter onFilterChange={handleFilterChange} />
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
